import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  Headphones,
  LoaderCircle,
  Pause,
  Play,
  RotateCcw,
  RotateCw
} from "lucide-react";
import { markLessonCompleted } from "../lib/courseProgress";
import { Brand } from "./Brand";

type ClientSegment =
  | {
      type: "speech";
      clipId: string;
      pauseAfter: number;
      voice?: string;
    }
  | { type: "pause"; milliseconds: number };

type LessonManifest = {
  lesson: {
    id: number;
    sectionId: number;
    title: string;
    scene: string;
    durationMinutes: number;
    phrases: { phonetic: string; meaning: string; prompt: string }[];
    practice?: {
      label: string;
      prompts: string[];
    };
    review?: {
      label: string;
      lessonIds: number[];
      skills: string[];
    };
    dialogue: {
      speaker: string;
      phonetic: string;
      meaning: string;
      voice: string;
    }[];
  };
  segments: ClientSegment[];
};

type GeneratedLessonPlayerProps = {
  lessonId: number;
  creatorToken?: string;
  onClose: () => void;
};

const voiceNames: Record<string, string> = {
  narrator: "Abdullah",
  learner: "Learner",
  ammu: "Ammu",
  family: "Family"
};

export function GeneratedLessonPlayer({
  lessonId,
  creatorToken,
  onClose
}: GeneratedLessonPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const timerRef = useRef<number | null>(null);
  const [manifest, setManifest] = useState<LessonManifest | null>(null);
  const [segmentIndex, setSegmentIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState("");
  const [showTranscript, setShowTranscript] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`/api/lessons/${lessonId}/manifest`, {
      headers: creatorToken
        ? { Authorization: `Bearer ${creatorToken}` }
        : undefined,
      signal: controller.signal
    })
      .then(async response => {
        const data = await response.json();
        if (!response.ok) throw new Error(data?.error || "Lesson could not load.");
        setManifest(data);
      })
      .catch(loadError => {
        if (loadError?.name !== "AbortError") {
          setError(loadError instanceof Error ? loadError.message : "Lesson could not load.");
        }
      });
    return () => controller.abort();
  }, [creatorToken, lessonId]);

  const segment = manifest?.segments[segmentIndex];
  const progress = manifest
    ? ((segmentIndex + 1) / Math.max(manifest.segments.length, 1)) * 100
    : 0;

  const activePhrase = useMemo(() => {
    if (!manifest?.lesson.phrases.length) return null;
    const position = segmentIndex / Math.max(manifest.segments.length - 1, 1);
    const phraseIndex = Math.min(
      manifest.lesson.phrases.length - 1,
      Math.floor(position * manifest.lesson.phrases.length)
    );
    return manifest.lesson.phrases[phraseIndex];
  }, [manifest, segmentIndex]);

  const clearTimer = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const advance = () => {
    clearTimer();
    setSegmentIndex(current => {
      if (!manifest || current >= manifest.segments.length - 1) {
        setPlaying(false);
        if (manifest) markLessonCompleted(lessonId);
        return current;
      }
      return current + 1;
    });
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!manifest || !segment || !playing || !audio) return;
    clearTimer();

    if (segment.type === "pause") {
      audio.pause();
      timerRef.current = window.setTimeout(advance, segment.milliseconds);
      return clearTimer;
    }

    const src = `/api/lessons/${lessonId}/audio/${segment.clipId}`;
    if (!audio.src.endsWith(src)) audio.src = src;
    void audio.play().catch(() => {
      setPlaying(false);
      setError("Tap play once more to continue the lesson.");
    });

    return clearTimer;
  }, [lessonId, manifest, playing, segment, segmentIndex]);

  useEffect(() => {
    if (!manifest) return;
    const next = manifest.segments
      .slice(segmentIndex + 1)
      .find(candidate => candidate.type === "speech");
    if (next?.type === "speech") {
      const preload = new Audio(`/api/lessons/${lessonId}/audio/${next.clipId}`);
      preload.preload = "auto";
    }
  }, [lessonId, manifest, segmentIndex]);

  useEffect(() => () => clearTimer(), []);

  const onEnded = () => {
    if (segment?.type === "speech" && segment.pauseAfter > 0) {
      timerRef.current = window.setTimeout(advance, segment.pauseAfter);
    } else {
      advance();
    }
  };

  const jump = (difference: number) => {
    clearTimer();
    audioRef.current?.pause();
    setSegmentIndex(current =>
      Math.min(
        Math.max(0, current + difference),
        Math.max((manifest?.segments.length ?? 1) - 1, 0)
      )
    );
  };

  if (error && !manifest) {
    return (
      <main className="generated-player generated-player--loading">
        <button className="icon-button" type="button" onClick={onClose} aria-label="Back">
          <ArrowLeft />
        </button>
        <p role="alert">{error}</p>
      </main>
    );
  }

  if (!manifest || !activePhrase) {
    return (
      <main className="generated-player generated-player--loading">
        <LoaderCircle className="spin" aria-hidden="true" />
        <p>Loading lesson audio…</p>
      </main>
    );
  }

  return (
    <main className="generated-player">
      <audio ref={audioRef} onEnded={onEnded} onError={() => setError("One audio clip could not load.")} />
      <header className="player-header">
        <button className="icon-button" type="button" onClick={onClose} aria-label="Back">
          <ArrowLeft />
        </button>
        <Brand compact />
        <span className="player-header__spacer" aria-hidden="true" />
      </header>

      <section className="sequence-progress" aria-label="Lesson progress">
        <span>Step {segmentIndex + 1}</span>
        <progress value={progress} max="100" />
        <span>{Math.round(progress)}%</span>
      </section>

      <section className="player-title">
        <p className="eyebrow">Section {manifest.lesson.sectionId} · Lesson {manifest.lesson.id}</p>
        <h1>{manifest.lesson.title}</h1>
        <p>{manifest.lesson.scene}</p>
      </section>

      <section className="generated-stage" aria-live="polite">
        <span className={segment?.type === "pause" ? "generated-stage__turn" : ""}>
          {segment?.type === "pause"
            ? "Your turn"
            : voiceNames[segment?.voice ?? "narrator"] || "Listen"}
        </span>
        <h2>{activePhrase.phonetic}</h2>
        <p>{activePhrase.meaning}</p>
        {segment?.type === "pause" && <strong>{activePhrase.prompt}</strong>}
      </section>

      <section className="transport-controls" aria-label="Playback controls">
        <button type="button" onClick={() => jump(-5)} aria-label="Back five steps">
          <RotateCcw aria-hidden="true" />
          <span>5</span>
        </button>
        <button
          type="button"
          className="transport-controls__main"
          onClick={() => {
            if (playing) {
              clearTimer();
              audioRef.current?.pause();
            }
            setPlaying(value => !value);
          }}
          aria-label={playing ? "Pause lesson" : "Play lesson"}
        >
          {playing ? <Pause fill="currentColor" /> : <Play fill="currentColor" />}
        </button>
        <button type="button" onClick={() => jump(5)} aria-label="Forward five steps">
          <RotateCw aria-hidden="true" />
          <span>5</span>
        </button>
      </section>

      {error && <p className="audio-error" role="alert">{error}</p>}

      <button
        className="sequence-transcript-toggle"
        type="button"
        onClick={() => setShowTranscript(value => !value)}
      >
        <Headphones aria-hidden="true" />
        {showTranscript ? "Hide conversation" : "Show conversation"}
      </button>

      {showTranscript && (
        <section className="transcript">
          <p className="eyebrow">Reference</p>
          <h2>Today&apos;s new language</h2>
          <div className="lesson-language-list">
            {manifest.lesson.phrases.map((phrase, index) => (
              <article key={`${phrase.phonetic}-${index}`}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <strong>{phrase.phonetic}</strong>
                  <small>{phrase.meaning}</small>
                </div>
              </article>
            ))}
          </div>

          {manifest.lesson.practice ? (
            <div className="lesson-practice-summary">
              <h3>{manifest.lesson.practice.label}</h3>
              <ul>
                {manifest.lesson.practice.prompts.slice(0, 6).map(prompt => (
                  <li key={prompt}>{prompt}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {manifest.lesson.review ? (
            <p className="lesson-review-summary">
              <strong>{manifest.lesson.review.label}:</strong>{" "}
              {manifest.lesson.review.skills.join(", ")}
              {manifest.lesson.review.lessonIds.length
                ? ` · lessons ${manifest.lesson.review.lessonIds.join(", ")}`
                : ""}
            </p>
          ) : null}

          <h2>Today&apos;s conversation</h2>
          {manifest.lesson.dialogue.map((line, index) => (
            <article className="dialogue-line" key={`${line.speaker}-${index}`}>
              <span>{line.speaker}</span>
              <p>{line.phonetic}</p>
              <small>{line.meaning}</small>
            </article>
          ))}
        </section>
      )}
    </main>
  );
}
